import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Users,
  Search,
  Filter,
  Edit,
  Save,
  X,
  Shield,
  Crown,
  UserCheck,
  AlertTriangle,
  CheckCircle,
  Eye,
  EyeOff,
  RefreshCw,
  Trash2,
  Plus,
  Settings,
  History,
  Lock
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '../../contexts/AuthContext';
import { safeParseObject } from '../../utils/storageUtils.js';
import UserHistoryViewer from './UserHistoryViewer';

/**
 * Componente de Gestão de Acessos
 * Permite ao administrador gerenciar níveis de acesso, permissões e usuários
 */
const AccessManagement = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterLevel, setFilterLevel] = useState('all');
  const [editingUser, setEditingUser] = useState(null);
  const [viewingHistory, setViewingHistory] = useState(null); // User whose history is being viewed
  const [showPasswords, setShowPasswords] = useState({});
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  // Definição dos níveis de acesso disponíveis
  const accessLevels = {
    'user': {
      label: 'Usuário',
      description: 'Usuário comum - pode criar tickets',
      color: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
      icon: Users,
      hierarchy: 0
    },
    'suporte': {
      label: 'Suporte',
      description: 'Especialista em dúvidas gerais',
      color: 'bg-green-500/20 text-green-400 border-green-500/30',
      icon: UserCheck,
      hierarchy: 1
    },
    'suporte_compras': {
      label: 'Suporte Compras',
      description: 'Especialista em compras e doações',
      color: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      icon: UserCheck,
      hierarchy: 1
    },
    'suporte_descontos': {
      label: 'Suporte Descontos',
      description: 'Especialista em cupons e descontos',
      color: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
      icon: UserCheck,
      hierarchy: 1
    },
    'moderador': {
      label: 'Moderador',
      description: 'Moderador de denúncias',
      color: 'bg-red-500/20 text-red-400 border-red-500/30',
      icon: Shield,
      hierarchy: 1
    },
    'super_moderador': {
      label: 'Super Moderador',
      description: 'Moderador de contestações',
      color: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
      icon: Shield,
      hierarchy: 1
    },
    'programador': {
      label: 'Programador',
      description: 'Desenvolvedor técnico',
      color: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
      icon: Settings,
      hierarchy: 1
    },
    'head_suporte': {
      label: 'Head de Suporte',
      description: 'Supervisiona departamento de suporte',
      color: 'bg-green-600/20 text-green-300 border-green-600/30',
      icon: Crown,
      hierarchy: 2
    },
    'head_moderacao': {
      label: 'Head de Moderação',
      description: 'Supervisiona departamento de moderação',
      color: 'bg-red-600/20 text-red-300 border-red-600/30',
      icon: Crown,
      hierarchy: 2
    },
    'head_staff': {
      label: 'Head de Staff',
      description: 'Supervisiona todos os departamentos',
      color: 'bg-purple-600/20 text-purple-300 border-purple-600/30',
      icon: Crown,
      hierarchy: 3
    },
    'admin': {
      label: 'Administrador',
      description: 'Acesso completo ao sistema',
      color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      icon: Crown,
      hierarchy: 4
    }
  };

  // Carregar usuários ao montar o componente
  useEffect(() => {
    loadUsers();
  }, []);

  // Aplicar filtros
  useEffect(() => {
    let filtered = users;

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(user =>
        user.name?.toLowerCase().includes(term) ||
        user.email?.toLowerCase().includes(term) ||
        user.discordId?.toLowerCase().includes(term)
      );
    }

    if (filterLevel !== 'all') {
      filtered = filtered.filter(user => user.accessLevel === filterLevel);
    }

    setFilteredUsers(filtered);
  }, [users, searchTerm, filterLevel]);

  // Função para carregar usuários
  const loadUsers = () => {
    setLoading(true);
    try {
      // Carregar usuários do localStorage
      const registeredUsers = safeParseObject('registeredUsers');

      // Carregar alterações salvas para usuários pré-definidos
      const predefinedChanges = safeParseObject('predefinedUserChanges');

      // Usuários pré-configurados (admin e testes)
      const predefinedUsers = {
        '401033888303939584': {
          id: 1,
          discordId: '401033888303939584',
          name: 'Administrador',
          email: 'admin@grupocapital.com',
          phone: '(11) 99999-0000',
          accessLevel: predefinedChanges['401033888303939584']?.accessLevel || 'admin',
          cities: [
            { city: 'CAPITAL', playerId: 'ADM001', characterName: 'Admin_Capital' },
            { city: 'VALLEY NORTE', playerId: 'ADM002', characterName: 'Admin_Valley' },
            { city: 'VALLEY SUL', playerId: 'ADM003', characterName: 'Admin_Sul' },
            { city: 'MORADA DO VALLEY', playerId: 'ADM004', characterName: 'Admin_Morada' }
          ],
          allowedCities: predefinedChanges['401033888303939584']?.allowedCities || ['CAPITAL', 'VALLEY NORTE', 'VALLEY SUL', 'MORADA DO VALLEY'],
          createdAt: '2024-01-01',
          password: '22051997@',
          isPredefined: true
        },
        'test_suporte': {
          id: 10,
          discordId: 'test_suporte',
          name: 'João Suporte',
          email: 'suporte@grupocapital.com',
          phone: '(11) 99999-0010',
          accessLevel: predefinedChanges['test_suporte']?.accessLevel || 'suporte',
          cities: [{ city: 'CAPITAL', playerId: 'SUP001', characterName: 'Joao_Suporte' }],
          allowedCities: predefinedChanges['test_suporte']?.allowedCities || ['CAPITAL', 'VALLEY NORTE'],
          createdAt: '2024-01-01',
          password: '123456',
          isPredefined: true
        },
        'test_moderador': {
          id: 13,
          discordId: 'test_moderador',
          name: 'Ana Moderadora',
          email: 'moderador@grupocapital.com',
          phone: '(11) 99999-0013',
          accessLevel: predefinedChanges['test_moderador']?.accessLevel || 'moderador',
          cities: [{ city: 'CAPITAL', playerId: 'MOD001', characterName: 'Ana_Moderadora' }],
          allowedCities: predefinedChanges['test_moderador']?.allowedCities || ['CAPITAL'],
          createdAt: '2024-01-01',
          password: '123456',
          isPredefined: true
        },
        'test_programador': {
          id: 15,
          discordId: 'test_programador',
          name: 'Lucas Programador',
          email: 'dev@grupocapital.com',
          phone: '(11) 99999-0015',
          accessLevel: predefinedChanges['test_programador']?.accessLevel || 'programador',
          cities: [{ city: 'CAPITAL', playerId: 'DEV001', characterName: 'Lucas_Dev' }],
          allowedCities: predefinedChanges['test_programador']?.allowedCities || ['CAPITAL', 'VALLEY NORTE', 'VALLEY SUL', 'MORADA DO VALLEY'],
          createdAt: '2024-01-01',
          password: '123456',
          isPredefined: true
        }
      };

      // Combinar usuários
      const allUsers = { ...predefinedUsers, ...registeredUsers };
      const usersList = Object.values(allUsers);

      setUsers(usersList);
      setMessage({ type: 'success', text: `${usersList.length} usuários carregados com sucesso` });
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
      setMessage({ type: 'error', text: 'Erro ao carregar usuários' });
    } finally {
      setLoading(false);
    }
  };

  // Função para salvar alterações do usuário
  const saveUserChanges = async (userData) => {
    setLoading(true);
    try {
      // Para usuários pré-definidos, permitir apenas edição de nível de acesso e cidades permitidas
      if (userData.isPredefined) {
        const allowedChanges = ['accessLevel', 'allowedCities'];
        const hasOnlyAllowedChanges = Object.keys(userData).every(key =>
          allowedChanges.includes(key) || userData[key] === editingUser[key]
        );

        if (!hasOnlyAllowedChanges && editingUser.accessLevel === userData.accessLevel &&
          JSON.stringify(editingUser.allowedCities) === JSON.stringify(userData.allowedCities)) {
          setMessage({ type: 'error', text: 'Usuários pré-definidos só podem ter nível de acesso e cidades alterados' });
          return;
        }
      }

      // Validações
      if (!userData.name || !userData.email || !userData.accessLevel) {
        setMessage({ type: 'error', text: 'Todos os campos obrigatórios devem ser preenchidos' });
        return;
      }

      // Atualizar usuário na lista
      const updatedUsers = users.map(u =>
        u.discordId === userData.discordId ? { ...u, ...userData } : u
      );
      setUsers(updatedUsers);

      // Salvar no localStorage
      if (!userData.isPredefined) {
        // Para usuários normais, salvar tudo
        const registeredUsers = safeParseObject('registeredUsers');
        registeredUsers[userData.discordId] = userData;
        localStorage.setItem('registeredUsers', JSON.stringify(registeredUsers));
      } else {
        // Para usuários pré-definidos, salvar apenas as alterações permitidas
        const predefinedChanges = safeParseObject('predefinedUserChanges');
        predefinedChanges[userData.discordId] = {
          accessLevel: userData.accessLevel,
          allowedCities: userData.allowedCities || []
        };
        localStorage.setItem('predefinedUserChanges', JSON.stringify(predefinedChanges));
      }

      setEditingUser(null);
      setMessage({ type: 'success', text: 'Usuário atualizado com sucesso' });
    } catch (error) {
      console.error('Erro ao salvar usuário:', error);
      setMessage({ type: 'error', text: 'Erro ao salvar alterações' });
    } finally {
      setLoading(false);
    }
  };

  // Função para resetar senha
  const resetPassword = (userId) => {
    const newPassword = Math.random().toString(36).slice(-8);
    const updatedUsers = users.map(u =>
      u.discordId === userId ? { ...u, password: newPassword } : u
    );
    setUsers(updatedUsers);

    // Salvar no localStorage
    const user = updatedUsers.find(u => u.discordId === userId);
    if (!user.isPredefined) {
      const registeredUsers = safeParseObject('registeredUsers');
      registeredUsers[userId] = user;
      localStorage.setItem('registeredUsers', JSON.stringify(registeredUsers));
    }

    setMessage({ type: 'success', text: `Nova senha gerada: ${newPassword}` });
  };

  // Função para alternar visualização de senha
  const togglePasswordVisibility = (userId) => {
    setShowPasswords(prev => ({
      ...prev,
      [userId]: !prev[userId]
    }));
  };

  // Função para obter estatísticas
  const getStats = () => {
    const stats = {
      total: users.length,
      byLevel: {},
      byHierarchy: { 0: 0, 1: 0, 2: 0, 3: 0, 4: 0 }
    };

    users.forEach(user => {
      const level = user.accessLevel;
      stats.byLevel[level] = (stats.byLevel[level] || 0) + 1;

      const hierarchy = accessLevels[level]?.hierarchy || 0;
      stats.byHierarchy[hierarchy]++;
    });

    return stats;
  };

  const stats = getStats();

  // Lista de cidades disponíveis
  const availableCities = ['CAPITAL', 'VALLEY NORTE', 'VALLEY SUL', 'MORADA DO VALLEY'];

  // Componente de edição de usuário
  const UserEditDialog = ({ user, onSave, onCancel }) => {
    const [formData, setFormData] = useState({
      name: user.name || '',
      email: user.email || '',
      phone: user.phone || '',
      accessLevel: user.accessLevel || 'user',
      allowedCities: user.allowedCities || []
    });

    const handleSave = () => {
      onSave({ ...user, ...formData });
    };

    // Função para alternar cidade na lista de permitidas
    const toggleCity = (city) => {
      setFormData(prev => ({
        ...prev,
        allowedCities: prev.allowedCities.includes(city)
          ? prev.allowedCities.filter(c => c !== city)
          : [...prev.allowedCities, city]
      }));
    };

    return (
      <Dialog open={true} onOpenChange={onCancel}>
        <DialogContent className="bg-[#1a1a2e] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle>Editar Usuário</DialogTitle>
            <DialogDescription className="text-gray-400">
              Altere as informações do usuário {user.name}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-400">Nome</label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className="bg-white/5 border-white/10 text-white"
                disabled={user.isPredefined}
              />
            </div>

            <div>
              <label className="text-sm font-medium text-gray-400">Email</label>
              <Input
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                className="bg-white/5 border-white/10 text-white"
                disabled={user.isPredefined}
              />
            </div>

            <div>
              <label className="text-sm font-medium text-gray-400">Telefone</label>
              <Input
                value={formData.phone}
                onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                className="bg-white/5 border-white/10 text-white"
                disabled={user.isPredefined}
              />
            </div>

            <div>
              <label className="text-sm font-medium text-gray-400">Nível de Acesso</label>
              <Select value={formData.accessLevel} onValueChange={(value) => setFormData(prev => ({ ...prev, accessLevel: value }))}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(accessLevels).map(([key, level]) => (
                    <SelectItem key={key} value={key}>
                      <div className="flex items-center space-x-2">
                        <level.icon className="h-4 w-4" />
                        <span>{level.label}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Seção de Cidades Permitidas - apenas para staff */}
            {formData.accessLevel !== 'user' && formData.accessLevel !== 'admin' && (
              <div>
                <label className="text-sm font-medium text-gray-400 mb-3 block">
                  Cidades Permitidas para Atendimento
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {availableCities.map((city) => (
                    <div
                      key={city}
                      onClick={() => toggleCity(city)}
                      className={`p-3 rounded-lg border cursor-pointer transition-all duration-200 ${formData.allowedCities.includes(city)
                        ? 'bg-blue-500/20 border-blue-500/50 text-blue-400'
                        : 'bg-white/5 border-white/10 text-gray-400 hover:bg-white/10'
                        }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">{city}</span>
                        {formData.allowedCities.includes(city) && (
                          <CheckCircle className="h-4 w-4" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Selecione as cidades que este usuário pode atender como staff
                </p>
              </div>
            )}
          </div>

          <div className="flex justify-end space-x-2 mt-6">
            <Button variant="outline" onClick={onCancel} className="border-white/10 text-white hover:bg-white/10">
              Cancelar
            </Button>
            <Button onClick={handleSave} className="bg-blue-600 hover:bg-blue-700">
              <Save className="h-4 w-4 mr-2" />
              Salvar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between"
      >
        <div>
          <h1 className="text-3xl font-bold text-white mb-2 flex items-center">
            <Shield className="h-8 w-8 mr-3 text-blue-400" />
            Gestão de Acessos
          </h1>
          <p className="text-gray-400">
            Gerencie níveis de acesso e permissões dos usuários
          </p>
        </div>

        <div className="flex items-center space-x-2 mt-4 sm:mt-0">
          <Button
            onClick={loadUsers}
            disabled={loading}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
        </div>
      </motion.div>

      {/* Mensagem de feedback */}
      {message.text && (
        <Alert className={`${message.type === 'error' ? 'border-red-500/50 bg-red-500/10' : 'border-green-500/50 bg-green-500/10'}`}>
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription className={message.type === 'error' ? 'text-red-400' : 'text-green-400'}>
            {message.text}
          </AlertDescription>
        </Alert>
      )}

      {/* Estatísticas */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4"
      >
        <Card className="bg-black/20 border-white/10">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-400">Total</p>
                <p className="text-2xl font-bold text-white">{stats.total}</p>
              </div>
              <Users className="h-8 w-8 text-blue-400" />
            </div>
          </CardContent>
        </Card>

        {[0, 1, 2, 3, 4].map(level => (
          <Card key={level} className="bg-black/20 border-white/10">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-400">Nível {level}</p>
                  <p className="text-2xl font-bold text-white">{stats.byHierarchy[level]}</p>
                </div>
                <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg flex items-center justify-center text-white font-bold">
                  {level}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </motion.div>

      {/* Filtros */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
        className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4"
      >
        <div className="flex-1">
          <Input
            placeholder="Buscar por nome, email ou Discord ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="bg-white/5 border-white/10 text-white placeholder-gray-500"
          />
        </div>
        <Select value={filterLevel} onValueChange={setFilterLevel}>
          <SelectTrigger className="w-full sm:w-48 bg-white/5 border-white/10 text-white">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os Níveis</SelectItem>
            {Object.entries(accessLevels).map(([key, level]) => (
              <SelectItem key={key} value={key}>
                {level.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </motion.div>

      {/* Lista de usuários */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.3 }}
      >
        <Card className="bg-black/20 border-white/10">
          <CardHeader>
            <CardTitle className="text-white">Usuários do Sistema</CardTitle>
            <CardDescription className="text-gray-400">
              Lista completa de usuários com seus respectivos níveis de acesso
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">
                <RefreshCw className="h-8 w-8 text-blue-400 mx-auto mb-4 animate-spin" />
                <p className="text-gray-400">Carregando usuários...</p>
              </div>
            ) : filteredUsers.length > 0 ? (
              <div className="space-y-4">
                {filteredUsers.map((userData) => {
                  const levelInfo = accessLevels[userData.accessLevel] || accessLevels.user;
                  const LevelIcon = levelInfo.icon;

                  return (
                    <motion.div
                      key={userData.discordId}
                      whileHover={{ scale: 1.01 }}
                      className="p-4 bg-white/5 rounded-lg border border-white/10 hover:bg-white/10 transition-all duration-200"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg flex items-center justify-center">
                            <LevelIcon className="h-6 w-6 text-white" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-white">{userData.name}</h3>
                            <p className="text-sm text-gray-400">{userData.email}</p>
                            <p className="text-xs text-gray-500">Discord ID: {userData.discordId}</p>
                          </div>
                        </div>

                        <div className="flex items-center space-x-3">
                          <Badge className={levelInfo.color}>
                            {levelInfo.label}
                          </Badge>

                          {userData.isPredefined && (
                            <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">
                              Sistema
                            </Badge>
                          )}

                          <div className="flex items-center space-x-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setViewingHistory(userData)}
                              className="text-purple-400 hover:text-purple-300 hover:bg-purple-500/10"
                              title="Ver Histórico"
                            >
                              <History className="h-4 w-4" />
                            </Button>

                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => togglePasswordVisibility(userData.discordId)}
                              className="text-gray-400 hover:text-white hover:bg-white/10"
                            >
                              {showPasswords[userData.discordId] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </Button>

                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setEditingUser(userData)}
                              className="text-blue-400 hover:text-blue-300 hover:bg-blue-500/10"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>

                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => resetPassword(userData.discordId)}
                              className="text-orange-400 hover:text-orange-300 hover:bg-orange-500/10"
                            >
                              <RefreshCw className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>

                      {showPasswords[userData.discordId] && (
                        <div className="mt-3 p-3 bg-black/20 rounded border border-white/10">
                          <p className="text-sm text-gray-400">
                            <strong>Senha:</strong> <span className="font-mono text-white">{userData.password}</span>
                          </p>
                        </div>
                      )}

                      <div className="mt-3 text-xs text-gray-500">
                        <p><strong>Telefone:</strong> {userData.phone || 'Não informado'}</p>
                        <p><strong>Cadastrado em:</strong> {new Date(userData.createdAt).toLocaleDateString('pt-BR')}</p>
                        <p><strong>Personagens:</strong> {userData.cities?.length || 0} cidade(s)</p>
                        {userData.allowedCities && userData.allowedCities.length > 0 && (
                          <p><strong>Cidades Staff:</strong> {userData.allowedCities.join(', ')}</p>
                        )}
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8">
                <Users className="h-12 w-12 text-gray-500 mx-auto mb-4" />
                <p className="text-gray-400">Nenhum usuário encontrado</p>
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>

      {/* Dialog de edição */}
      {editingUser && (
        <UserEditDialog
          user={editingUser}
          onSave={saveUserChanges}
          onCancel={() => setEditingUser(null)}
        />
      )}

      {/* Dialog de histórico */}
      {viewingHistory && (
        <Dialog open={true} onOpenChange={() => setViewingHistory(null)}>
          <DialogContent className="bg-[#1a1a2e] border-white/10 text-white max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <History className="h-5 w-5" />
                Histórico de Alterações
              </DialogTitle>
              <DialogDescription className="text-gray-400">
                Visualize o histórico completo de mudanças do usuário
              </DialogDescription>
            </DialogHeader>

            <UserHistoryViewer
              userId={viewingHistory.discordId}
              userName={viewingHistory.name}
            />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default AccessManagement;
